import { gql, gqlFetch, invalidateGqlCacheById } from '@/service/gql'
import { trackFieldsFrag } from '@/track/trackFieldsFrag'
import type { SanitizedGql } from '@/util/gqlUtil'
import type { ResultOf } from 'gql.tada'
import { getAuth } from '@/auth/authRef'
import { playlistFieldsFrag } from '@/playlistdetail/playlistFieldsFrag'
import { invalidateLibraryHomeCache } from '@/library/libraryService'

const playlistTracksFrag = gql(
    `
        fragment playlistTracks on Playlist @_unmask {
            tracks(first: $first, after: $after) {
                edges {
                    cursor
                    node {
                        ...trackFields
                    }
                }
                edgeCount
                pageInfo {
                    hasNextPage
                    token
                }
            }
        }
    `,
    [trackFieldsFrag]
)

const playlistDetailQuery = gql(
    `
        query playlistDetail(
            $id: String!
            $isAuthenticated: Boolean!
        ) {
            playlist(id: $id) {
                ...playlistFields
            }
        }
    `,
    [playlistFieldsFrag]
)

export type PlaylistDetailResult = SanitizedGql<ResultOf<typeof playlistDetailQuery>>

const playlistTracksQuery = gql(
    `
        query playlistDetail(
            $id: String!
            $first: Float
            $after: String
            $isAuthenticated: Boolean!
        ) {
            playlist(id: $id) {
                ...playlistTracks
            }
        }
    `,
    [playlistTracksFrag]
)

export type PlaylistTracksResult = SanitizedGql<ResultOf<typeof playlistTracksQuery>>

export type PlaylistDetailOptions = {
    readonly id: string
}

function detailCacheId(playlistId: string): string {
    // Stable id so mutations can invalidate the query — the default
    // hash-derived id would mean we'd need to know the variables shape
    // to clear it.
    return `playlistDetail:${playlistId}`
}

function tracksCacheId(playlistId: string): string {
    // Tracks pages share one base id; flushing it invalidates every
    // page (the cache stores them by full key, which includes our
    // override + the variables hash).
    return `playlistTracks:${playlistId}`
}

export async function fetchPlaylistDetailPage(
    options: PlaylistDetailOptions
): Promise<PlaylistDetailResult> {
    return gqlFetch(
        playlistDetailQuery,
        {
            id: options.id,
            isAuthenticated: getAuth().isSignedIn(),
        },
        {
            cache: true,
            cacheId: detailCacheId(options.id),
        }
    )
}

/**
 * Drop the cached detail + tracks payloads for the given playlist.
 * Call after any mutation that changes the playlist content (append,
 * remove, move, rename) so the next read goes back to the gateway.
 */
async function invalidatePlaylistCaches(playlistId: string): Promise<void> {
    await Promise.all([
        invalidateGqlCacheById(detailCacheId(playlistId)),
        invalidateGqlCacheById(tracksCacheId(playlistId)),
    ])
}

export type PlaylistTracksOptions = {
    readonly id: string
    readonly offset: number
    readonly count: number
}

export async function fetchPlaylistTracks(
    options: PlaylistTracksOptions
): Promise<PlaylistTracksResult> {
    // Firefly's MusicPlaylistService rejects an `after` token on the
    // first page with `InvalidPageTokenException`. Pass null when
    // offset is 0; only later pages get the `offset:N` cursor.
    const after = options.offset > 0 ? `offset: ${options.offset}` : null
    return gqlFetch(
        playlistTracksQuery,
        {
            id: options.id,
            first: options.count,
            after,
            isAuthenticated: getAuth().isSignedIn(),
        },
        { cache: true, cacheId: tracksCacheId(options.id) }
    )
}

const createPlaylistMutation = gql(`
    mutation createPlaylist(
        $title: String!
        $visibility: String
        $trackAsins: [String]
    ) {
        createPlaylist(
            title: $title
            visibility: $visibility
            trackAsins: $trackAsins
        ) {
            id
            title
            url
            visibility
        }
    }
`)

const updatePlaylistMutation = gql(`
    mutation updatePlaylistProperties($id: String!, $title: String, $description: String) {
        updatePlaylist(playlistId: $id, title: $title, description: $description) {
            id
            title
            description
        }
    }
`)

const deletePlaylistMutation = gql(`
    mutation deletePlaylist($id: String!) {
        deletePlaylist(id: $id) {
            id
            isDeleted
        }
    }
`)

const appendTracksMutation = gql(`
    mutation appendTracksToPlaylist($id: String!, $trackIds: [String]) {
        appendTracks(playlistId: $id, trackIds: $trackIds) {
            id
            trackCount
        }
    }
`)

const removeTracksMutation = gql(`
    mutation removeTracksFromPlaylist($id: String!, $entryIds: [String]) {
        removeTracks(playlistId: $id, entryIds: $entryIds) {
            id
            trackCount
        }
    }
`)

const moveTracksMutation = gql(`
    mutation movePlaylistTrack(
        $id: String!
        $entryIds: [String]
        $entryIdAbove: String
        $entryIdBelow: String
    ) {
        moveTracks(
            playlistId: $id
            entryIds: $entryIds
            entryIdAbove: $entryIdAbove
            entryIdBelow: $entryIdBelow
        ) {
            id
        }
    }
`)

export type PlaylistVisibility = 'PUBLIC' | 'PRIVATE'

export interface CreatePlaylistInput {
    readonly title: string
    readonly visibility?: PlaylistVisibility
    readonly trackAsins?: readonly string[]
}

/**
 * Creates a new playlist owned by the signed-in user. Returns the
 * server-assigned id so the caller can navigate straight to the
 * detail page (mirrors hornet's "create then push to /playlists/:id"
 * flow). Does not invalidate any cache — the new playlist isn't in
 * any existing list query yet.
 */
export async function createPlaylist(input: CreatePlaylistInput): Promise<{
    readonly id: string
    readonly title: string | null
    readonly url: string | null
} | null> {
    const result = await gqlFetch(createPlaylistMutation, {
        title: input.title,
        visibility: input.visibility ?? 'PRIVATE',
        trackAsins: input.trackAsins ? [...input.trackAsins] : null,
    })
    const created = result.createPlaylist
    if (!created?.id) return null
    // The user's library shovelers and MyPlaylists list both pull
    // from `fetchLibraryHome`, so a brand-new playlist won't show
    // up until that cache is dropped.
    await invalidateLibraryHomeCache()
    return {
        id: created.id,
        title: created.title ?? null,
        url: created.url ?? null,
    }
}

export interface UpdatePlaylistInput {
    readonly id: string
    readonly title?: string
    readonly description?: string
}

export async function updatePlaylistProperties(input: UpdatePlaylistInput): Promise<void> {
    await gqlFetch(updatePlaylistMutation, {
        id: input.id,
        title: input.title ?? null,
        description: input.description ?? null,
    })
    await invalidatePlaylistCaches(input.id)
    // The library shoveler shows the playlist title; rename has to
    // invalidate the parent list cache too.
    await invalidateLibraryHomeCache()
}

/**
 * Deletes the given playlist permanently. Resolves on success;
 * throws if the gateway reports `isDeleted: false` or the mutation
 * fails. Invalidates the playlist's cached detail + tracks so any
 * lingering references re-fetch on next view, plus the library home
 * caches so the deleted playlist drops from "Your Playlists".
 */
export async function deletePlaylist(playlistId: string): Promise<void> {
    const result = await gqlFetch(deletePlaylistMutation, { id: playlistId })
    if (result.deletePlaylist?.isDeleted !== true) {
        throw new Error('Server reported playlist was not deleted')
    }
    await invalidatePlaylistCaches(playlistId)
    await invalidateLibraryHomeCache()
}

export async function appendTracksToPlaylist(
    playlistId: string,
    trackIds: readonly string[]
): Promise<void> {
    if (trackIds.length === 0) return
    await gqlFetch(appendTracksMutation, {
        id: playlistId,
        trackIds: [...trackIds],
    })
    await invalidatePlaylistCaches(playlistId)
}

export async function removeTracksFromPlaylist(
    playlistId: string,
    entryIds: readonly string[]
): Promise<void> {
    if (entryIds.length === 0) return
    await gqlFetch(removeTracksMutation, {
        id: playlistId,
        entryIds: [...entryIds],
    })
    await invalidatePlaylistCaches(playlistId)
}

/**
 * Moves a track within a playlist. Hornet's pattern: the API positions a track
 * relative to a sibling — pass `entryIdAbove` to land just below that sibling
 * (used when moving to the very last position) or `entryIdBelow` to land just
 * above it (used otherwise).
 */
export async function moveTrackInPlaylist(input: {
    readonly playlistId: string
    readonly entryId: string
    readonly entryIdAbove?: string | null
    readonly entryIdBelow?: string | null
}): Promise<void> {
    await gqlFetch(moveTracksMutation, {
        id: input.playlistId,
        entryIds: [input.entryId],
        entryIdAbove: input.entryIdAbove ?? null,
        entryIdBelow: input.entryIdBelow ?? null,
    })
    await invalidatePlaylistCaches(input.playlistId)
}

/**
 * Cursors come back from Firefly as `<encoded>:<entryId>`. Hornet strips the
 * prefix to get the bare entry id used by move/remove mutations.
 */
export function entryIdFromCursor(cursor: string | null | undefined): string | null {
    if (!cursor) return null
    const idx = cursor.indexOf(':')
    return idx >= 0 ? cursor.substring(idx + 1) : cursor
}

const playlistCapabilitiesQuery = gql(`
    query playlistCapabilities($id: String!) {
        playlist(id: $id) {
            id
            trackCount
            capabilities {
                appendTracks {
                    isAllowed
                    reason
                }
            }
        }
    }
`)

export interface PlaylistCapabilities {
    readonly trackCount: number
    readonly appendTracks: { readonly isAllowed: boolean; readonly reason: string | null }
}

export async function fetchPlaylistCapabilities(
    playlistId: string
): Promise<PlaylistCapabilities | null> {
    const result = await gqlFetch(playlistCapabilitiesQuery, {
        id: playlistId,
    })
    const playlist = result.playlist
    if (!playlist) return null
    return {
        trackCount: playlist.trackCount ?? 0,
        appendTracks: {
            isAllowed: playlist.capabilities?.appendTracks?.isAllowed !== false,
            reason: playlist.capabilities?.appendTracks?.reason ?? null,
        },
    }
}

export function isDailyChangeLimitReached(capabilities: PlaylistCapabilities | null): boolean {
    if (!capabilities) return false
    if (capabilities.appendTracks.isAllowed) return false
    return capabilities.appendTracks.reason === 'AllAccessPlaylistTrackChangeLimitReached'
}
